import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Manager to-do list — stage 1 (D248, CJ 2026-08-08).
// Spec: docs/MANAGER-TODO-DASHBOARD-SPEC.md
//
// One screen with everything waiting, so nobody has to hunt between menus.
// Every row deep-links to the screen that resolves it, because "can't always
// search between menus" is the actual complaint.
//
// There is deliberately no tick box anywhere on this screen. Each row is a
// view of live state — approving the request is what removes it. A checkbox
// here would let someone clear a list without clearing the work.

const KIND_STYLE = {
  time_off_request:   { icon: '🗓', color: '#3f6f86' },
  availability_change:{ icon: '↔',  color: '#8a6d1f' },
  job_application:    { icon: '📄', color: '#5d4037' },
  incident:           { icon: '⚠',  color: '#c62828' },
  shift_handoff:      { icon: '🧹', color: '#a55c33' },
};

function ageLabel(days) {
  if (days == null) return '';
  if (days === 0) return 'today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

// CJ 2026-08-09: more than this many applicants in one café collapses behind a
// toggle. A run of near-identical applicant rows pushes the things that
// actually break — a certification, an unacknowledged handoff report — off the
// screen, and the point of this list is what breaks first.
const APPLICANTS_BEFORE_COLLAPSE = 3;


// One café's block. A top-level component, not declared inside
// ManagerTodoView's render: a component defined in a render body is a new type
// every render, so React remounts it and the expand/collapse state resets the
// moment anything else on the page changes.
function LocationGroup({ group: g, onAct, onDecide, busyId }) {
  const [showAll, setShowAll] = useState(false);
  const apps = g.items.filter((i) => i.kind === 'job_application');
  const collapsing = apps.length > APPLICANTS_BEFORE_COLLAPSE && !showAll;
  const hidden = collapsing ? new Set(apps.slice(APPLICANTS_BEFORE_COLLAPSE).map((i) => `${i.kind}-${i.id}`)) : new Set();
  const visible = g.items.filter((i) => !hidden.has(`${i.kind}-${i.id}`));
  const hiddenCount = apps.length - (collapsing ? APPLICANTS_BEFORE_COLLAPSE : apps.length);

  return (
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{g.location_name}</div>
          <div style={{ fontSize: 12, color: '#757575' }}>
            {g.count} item{g.count === 1 ? '' : 's'}
            {g.oldest_age_days >= 7 && (
              <span style={{ color: '#a8323a', fontWeight: 700 }}> · oldest {ageLabel(g.oldest_age_days)}</span>
            )}
          </div>
        </div>


        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {visible.map((it) => {
            const s = KIND_STYLE[it.kind] || { icon: '•', color: '#757575' };
            const late = it.due_in_days != null && it.due_in_days < 0;
            const stale = (it.age_days || 0) >= 7;
            return (
              <div key={`${it.kind}-${it.id}`}
                   style={{ display: 'flex', alignItems: 'flex-start', gap: 9,
                            border: '1px solid #eee', borderRadius: 8, padding: '8px 10px',
                            borderLeft: `3px solid ${it.urgency === 0 ? '#c62828' : it.urgency === 1 ? '#ef6c00' : '#e0e0e0'}` }}>
                <div style={{ fontSize: 15, lineHeight: '18px', color: s.color }}>{s.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{it.title}</div>
                  <div style={{ fontSize: 12, color: '#546e7a', marginTop: 1 }}>{it.detail}</div>
                  {/* Ageing is the whole accountability mechanism in stage 1:
                      with every manager seeing everything and nobody owning
                      anything, a row that is visibly old is the only pressure. */}
                  {(stale || late) && (
                    <div style={{ fontSize: 11.5, color: '#a8323a', fontWeight: 700, marginTop: 2 }}>
                      {late ? 'Past its date' : `Waiting ${ageLabel(it.age_days)}`}
                    </div>
                  )}
                </div>
                {/* A row that can be decided here gets the real action plus a
                    way through to the full screen. A row that cannot keeps the
                    single navigating button — but its label must not name an
                    action it does not perform. */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {it.act && (
                    <button onClick={() => onDecide(it)} className="btn-primary"
                            disabled={busyId === it.id}
                            style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                      {busyId === it.id
                        ? '…'
                        : it.act === 'approve_tor' ? 'Approve' : 'Acknowledge'}
                    </button>
                  )}
                  <button onClick={() => onAct(it)} className="btn-secondary"
                          style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                    {it.act ? 'Open' : it.action}
                  </button>
                </div>
              </div>
            );
          })}

          {/* Applicants beyond the first few fold away (CJ 2026-08-09). A run
              of near-identical applicant rows pushes the things that actually
              break off the screen. */}
          {(hiddenCount > 0 || showAll) && apps.length > APPLICANTS_BEFORE_COLLAPSE && (
            <button onClick={() => setShowAll((v) => !v)}
              style={{ background: 'none', border: '1px dashed #d0d4d9', borderRadius: 8,
                       padding: '7px 10px', fontSize: 12, fontWeight: 600, color: '#546e7a',
                       cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
              {showAll
                ? `Hide ${apps.length - APPLICANTS_BEFORE_COLLAPSE} applicant${apps.length - APPLICANTS_BEFORE_COLLAPSE === 1 ? '' : 's'}`
                : `▾ ${hiddenCount} more applicant${hiddenCount === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
  );
}

export function ManagerTodoView({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  async function load() {
    setErr('');
    try {
      setData(await api('GET', '/api/admin/manager-todo', null, headers));
    } catch (e) { setErr(e?.message || String(e)); }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (tab) => window.dispatchEvent(new CustomEvent('monarch:navigate', { detail: tab }));

  // Availability changes are the one row that cannot be cleared by fixing
  // something else — staff set their own availability, so there is nothing to
  // approve. Reviewing it records that a manager looked, then navigates. Every
  // other row still just navigates, because resolving the underlying thing is
  // what removes it.
  async function act(it) {
    if (it.kind === 'availability_change' && it.location_id) {
      try {
        await api('POST', '/api/admin/manager-todo/availability-reviewed',
          { location_id: it.location_id }, headers);
      } catch (_) {
        // Navigate anyway. Failing to record the review must not stop a manager
        // getting to the screen they asked for.
      }
      load();
    }
    go(it.link_tab);
  }

  // CJ 2026-08-13: "i approved Rielly's time off request in my to do list but
  // it didn't clear ... i had to enter it again there." The row's button said
  // "Approve or deny" and only navigated — the server saw exactly one approve
  // all day, the one he then made on the Schedule screen. A button that names
  // an action has to perform it.
  //
  // Only the frictionless decisions are inline. Denial needs a written reason
  // (the server rejects a note-less deny, deliberately — CJ 2026-08-02), and
  // approving past a deadline or without the PTO balance needs an override, so
  // those still open the full screen where the fields exist.
  const [busyId, setBusyId] = useState(null);
  async function decide(it) {
    const call = it.act === 'approve_tor'
      ? ['PUT',  `/api/tor/${it.id}/approve`]
      : ['POST', `/api/admin/shift-handoff/${it.id}/acknowledge`];
    setBusyId(it.id);
    try {
      await api(call[0], call[1], {}, headers);
      await load();
    } catch (e) {
      // A 409 here is the server refusing for a reason the manager has to see
      // and act on — a late submission needing an override, or not enough PTO
      // balance. Say why, then hand them to the screen that can resolve it.
      // Swallowing this is what made the old button feel like it had worked.
      alert(e?.message || String(e));
      go(it.link_tab);
    } finally {
      setBusyId(null);
    }
  }

  if (err) {
    return (
      <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
        <div className="text-sm" style={{ fontWeight: 600, marginBottom: 4 }}>The to-do list didn’t load</div>
        <div className="text-xs text-red-700" style={{ marginBottom: 10 }}>{err}</div>
        <button className="btn-secondary text-xs" onClick={load}>Try again</button>
      </div>
    );
  }
  if (!data) return <div className="card text-sm text-gray-500">Loading…</div>;

  return (
    <div className="space-y-3">
      <div className="card">
        <div className="text-base font-semibold">
          {data.total === 0
            ? 'Nothing waiting'
            /* Company-wide phrasing on purpose (D248): all managers see the
               same list, so "you have 14 things" would be a lie. */
            : `${data.total} waiting across ${data.cafes_with_items} café${data.cafes_with_items === 1 ? '' : 's'}`}
        </div>
        <div style={{ fontSize: 12, color: '#757575', marginTop: 2 }}>
          {data.total === 0
            ? 'Everything that needs a manager has been dealt with.'
            : 'Everything that needs a manager, soonest-breaking first. Every café is visible to every manager.'}
        </div>
      </div>

      {data.groups.map((g) => (
        <LocationGroup key={g.location_id ?? 'company'} group={g} onAct={act}
                       onDecide={decide} busyId={busyId} />
      ))}
    </div>
  );
}
